import React, { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { CheckCircle, XCircle, Spinner, EnvelopeSimpleOpen } from "@phosphor-icons/react";

export default function VerifyEmail() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const [state, setState] = useState({ loading: true, success: false, message: "", email: null });

  useEffect(() => {
    if (!token) {
      setState({ loading: false, success: false, message: "Verification token missing from the link." });
      return;
    }
    (async () => {
      try {
        const { data } = await api.post("/auth/verify-email", { token });
        setState({
          loading: false,
          success: true,
          email: data.email,
          alreadyVerified: !!data.already_verified,
          message: data.already_verified
            ? "Your email was already verified. You can sign in now."
            : "Email verified! You can now sign in to your account.",
        });
      } catch (e) {
        setState({
          loading: false,
          success: false,
          message: e.response?.data?.detail || "Verification failed.",
        });
      }
    })();
  }, [token]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-zinc-50 px-4" data-testid="verify-email-page">
      <div className="w-full max-w-md bg-white border border-zinc-200 rounded-2xl shadow-sm p-8 sm:p-10">
        <div className="flex flex-col items-center text-center">
          {state.loading ? (
            <>
              <Spinner size={48} className="text-[#002BF6] animate-spin" />
              <h1 className="text-xl font-semibold mt-5">Verifying your email…</h1>
              <p className="text-sm text-zinc-500 mt-2">Just a second.</p>
            </>
          ) : state.success ? (
            <>
              <div className="rounded-full bg-emerald-50 border border-emerald-200 p-3">
                {state.alreadyVerified
                  ? <EnvelopeSimpleOpen size={36} weight="bold" className="text-emerald-600" />
                  : <CheckCircle size={36} weight="bold" className="text-emerald-600" />}
              </div>
              <h1 className="text-2xl font-semibold mt-5" data-testid="verify-success-title">
                {state.alreadyVerified ? "Already verified" : "You're all set!"}
              </h1>
              <p className="text-sm text-zinc-600 mt-3">{state.message}</p>
              {state.email && (
                <p className="text-xs text-zinc-500 mt-1 font-mono">{state.email}</p>
              )}
              <Link
                to="/login"
                className="mt-6 inline-flex items-center justify-center bg-[#002BF6] hover:bg-[#0021C7] text-white font-medium px-5 py-2.5 rounded-md text-sm"
                data-testid="verify-go-to-login"
              >
                Go to login
              </Link>
            </>
          ) : (
            <>
              <div className="rounded-full bg-red-50 border border-red-200 p-3">
                <XCircle size={36} weight="bold" className="text-red-600" />
              </div>
              <h1 className="text-2xl font-semibold mt-5">Verification failed</h1>
              <p className="text-sm text-zinc-600 mt-3" data-testid="verify-error-message">
                {state.message}
              </p>
              <Link
                to="/login"
                className="mt-6 inline-flex items-center justify-center border border-zinc-300 hover:bg-zinc-50 text-zinc-900 font-medium px-5 py-2.5 rounded-md text-sm"
              >
                Back to login
              </Link>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
